import React, { useState, useEffect, useRef } from 'react';
import { InvitationDesign, ContentRow, TextStyle } from '../../_types/invitation-design-v2';
import { Event, InvitationType } from '../../_graphql/generated/operations';
import InvitationPreviewV2 from '../InvitationPreview/InvitationPreviewV2';
import InvitationTypeListStandalone from '../InvitationTypeList/InvitationTypeListStandalone';
import { INVITATION_TEMPLATES_V2 } from '../../_data/invitation-templates-v2';
import InlineElementControls from './InlineElementControls';
import DesignerControls from './DesignerControls';
import InvitationTypeForm from '../InvitationTypeForm/InvitationTypeForm';

interface InvitationDesignerV2Props {
  event: Event;
  initialDesign?: InvitationDesign;
  selectedInvitationType?: InvitationType;
  onSave: (design: InvitationDesign, invitationType?: InvitationType) => void;
  onCancel: () => void;
  hideTypeSelection?: boolean;
  isOnboarding?: boolean;
  forceTemplateSelection?: boolean;
  isNewInvitationType?: boolean;
}

// Default V2 design
const createDefaultDesign = (): InvitationDesign => ({
  version: '1.0.0',
  template: {
    id: 'default',
    name: 'Default Template',
    category: 'classic',
    theme: {
      primaryColor: '#8B7355',
      secondaryColor: '#D4A574',
      textColor: '#3A3A3A',
      accentColor: '#F5E6D3',
      fontFamily: 'Georgia, serif',
      secondaryFontFamily: 'Playfair Display, serif'
    },
    defaultTextStyle: {
      fontSize: 16,
      lineHeight: 1.6,
      letterSpacing: 0,
      textAlign: 'center',
      fontWeight: 'normal'
    },
    stylePresets: {
      title: {
        fontSize: 48,
        fontWeight: 'bold',
        fontFamily: '{{template.theme.secondaryFontFamily}}',
        marginBottom: 32,
        lineHeight: 1.2,
        letterSpacing: -1
      },
      subtitle: {
        fontSize: 24,
        fontWeight: 'normal',
        marginBottom: 20,
        lineHeight: 1.4,
        letterSpacing: 0.5
      },
      body: {
        fontSize: 16,
        lineHeight: 1.8,
        marginBottom: 16,
        fontWeight: 'normal'
      },
      accent: {
        fontSize: 14,
        fontStyle: 'italic',
        marginBottom: 12,
        lineHeight: 1.6,
        color: '{{template.theme.secondaryColor}}'
      }
    },
    background: {
      type: 'solid',
      value: '#FFFFF5'
    },
    border: {
      style: 'solid',
      width: 2,
      color: '#D4A574',
      radius: 0,
      pattern: undefined
    },
    margins: {
      top: 48,
      right: 36,
      bottom: 48,
      left: 36
    }
  },
  content: [
    {
      id: 'header-text',
      type: 'text',
      content: 'You are cordially invited',
      style: {
        fontSize: 14,
        textTransform: 'uppercase',
        letterSpacing: 2,
        marginBottom: 24
      }
    },
    {
      id: 'couple-names',
      type: 'text',
      content: 'Sarah & Michael',
      style: {
        fontSize: 48,
        fontFamily: '{{template.theme.secondaryFontFamily}}',
        marginBottom: 16
      }
    },
    {
      id: 'event-type',
      type: 'text',
      content: 'request the honor of your presence at their wedding',
      style: {
        fontSize: 18,
        fontStyle: 'italic',
        marginBottom: 32
      }
    },
    {
      id: 'divider-1',
      type: 'divider',
      style: {
        width: 100,
        height: 1,
        backgroundColor: '{{template.theme.primaryColor}}',
        marginBottom: 32
      }
    },
    {
      id: 'date-time',
      type: 'text',
      content: 'Saturday, June 15th, 2025\nat four o\'clock in the afternoon',
      style: {
        fontSize: 16,
        marginBottom: 24
      }
    },
    {
      id: 'location',
      type: 'text',
      content: 'The Grand Ballroom\nThe Ritz-Carlton\nNew York, New York',
      style: {
        fontSize: 16,
        marginBottom: 32
      }
    },
    {
      id: 'divider-2',
      type: 'divider',
      style: {
        width: 100,
        height: 1,
        backgroundColor: '{{template.theme.primaryColor}}',
        marginBottom: 32
      }
    },
    {
      id: 'reception',
      type: 'text',
      content: 'Reception to follow',
      style: {
        fontSize: 14,
        fontStyle: 'italic'
      }
    }
  ],
  metadata: {
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1,
    name: 'Wedding Invitation',
    tags: ['wedding', 'formal']
  }
});

const InvitationDesignerV2: React.FC<InvitationDesignerV2Props> = ({
  event,
  initialDesign,
  selectedInvitationType,
  onSave,
  onCancel,
  hideTypeSelection = false,
  isOnboarding = false,
  forceTemplateSelection = false,
  isNewInvitationType = false,
}) => {
  console.log('=== InvitationDesignerV2 render ===');
  console.log('initialDesign provided:', !!initialDesign);
  console.log('initialDesign content:', initialDesign);
  
  const [design, setDesign] = useState<InvitationDesign>(() => {
    const initialState = initialDesign || createDefaultDesign();
    console.log('Setting initial design state:', initialState);
    return initialState;
  });
  
  // Initialize with Full Invitation type as default
  const getDefaultInvitationType = () => {
    if (selectedInvitationType) return selectedInvitationType;
    
    // Look for Full Invitation type in event.invitationTypes
    if (event.invitationTypes && event.invitationTypes.length > 0) {
      const fullInvitationType = event.invitationTypes.find(type => type.name === 'Full Invitation');
      if (fullInvitationType) return fullInvitationType;
      
      // If no Full Invitation type found, return the first type
      return event.invitationTypes[0];
    }
    
    return undefined;
  };
  
  const defaultInvitationType = getDefaultInvitationType();
  
  // If we have a default invitation type or hideTypeSelection, start on styles tab
  // If forceTemplateSelection is true, always start with templates
  const [activeTab, setActiveTab] = useState<'types' | 'templates' | 'styles' | 'colors' | 'borders' | 'layout' | null>(
    forceTemplateSelection ? 'templates' : 
    (hideTypeSelection ? null : (defaultInvitationType ? null : 'types'))
  );
  const [currentInvitationType, setCurrentInvitationType] = useState<InvitationType | undefined>(defaultInvitationType);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedRowPosition, setSelectedRowPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [containerHeight, setContainerHeight] = useState(800);
  const [hasChanges, setHasChanges] = useState(false);
  const [showCreateInvitationTypeModal, setShowCreateInvitationTypeModal] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  
  // Store the initial design state - but we need to be careful about when this is set
  const [originalDesign] = useState(() => initialDesign || createDefaultDesign());
  
  // Track changes to design
  useEffect(() => {
    // Compare current design with original
    const currentDesignString = JSON.stringify(design);
    const originalDesignString = JSON.stringify(originalDesign);
    setHasChanges(currentDesignString !== originalDesignString);
  }, [design, originalDesign]);

  // Handle invitation type selection
  const handleInvitationTypeSelect = (invitationType: InvitationType) => {
    setCurrentInvitationType(invitationType);
    setActiveTab('templates');
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    console.log('=== handleTemplateSelect called ===');
    console.log('templateId:', templateId);
    console.log('initialDesign exists:', !!initialDesign);
    
    const template = INVITATION_TEMPLATES_V2.find(t => t.template.id === templateId);
    if (template) {
      console.log('Found template:', template.template.name);
      
      // If we have an initial design (saved design), only update the template but preserve content
      if (initialDesign) {
        console.log('PRESERVING content, only updating template because initialDesign exists');
        setDesign(prev => {
          const newDesign: InvitationDesign = {
            ...prev,
            template: {
              ...template.template
            },
            metadata: {
              ...prev.metadata!,
              name: prev.metadata?.name || 'Untitled Design',
              createdAt: prev.metadata?.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          };
          console.log('New design with preserved content:', newDesign);
          return newDesign;
        });
      } else {
        console.log('REPLACING entire design with template because no initialDesign');
        const newDesign = {
          ...template,
          metadata: {
            ...template.metadata!,
            name: template.metadata?.name || 'Untitled Design',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        };
        console.log('New design from template:', newDesign);
        setDesign(newDesign);
      }
      setSelectedTemplateId(templateId);
      console.log('Template selection complete');
    }
  };

  // Update a specific content row
  const updateContentRow = (rowId: string, updates: Partial<ContentRow>) => {
    setDesign(prev => ({
      ...prev,
      content: prev.content.map(row => 
        row.id === rowId ? { ...row, ...updates } : row
      )
    }));
  };

  // Update row content text
  const updateRowContent = (rowId: string, content: string) => {
    updateContentRow(rowId, { content });
  };

  // Handle when user starts editing (immediate change detection)
  const handleEditStart = () => {
    if (!hasChanges) {
      setHasChanges(true);
    }
  };

  // Update row style
  const updateRowStyle = (rowId: string, styleUpdates: Partial<TextStyle>) => {
    setDesign(prev => ({
      ...prev,
      content: prev.content.map(row => 
        row.id === rowId 
          ? { ...row, style: { ...row.style, ...styleUpdates } }
          : row
      )
    }));
  };


  // Update template theme
  const updateTheme = (themeUpdates: Partial<typeof design.template.theme>) => {
    setDesign(prev => ({
      ...prev,
      template: {
        ...prev.template,
        theme: {
          ...prev.template.theme,
          ...themeUpdates
        }
      }
    }));
  };

  // Update template settings
  const updateTemplate = (templateUpdates: Partial<typeof design.template>) => {
    setDesign(prev => ({
      ...prev,
      template: {
        ...prev.template,
        ...templateUpdates
      }
    }));
  };

  // Add a new content row
  const addContentRow = (afterId?: string) => {
    const newRow: ContentRow = {
      id: `row-${Date.now()}`,
      type: 'text',
      content: 'New text',
      style: {
        fontSize: 16,
        marginBottom: 16
      }
    };

    setDesign(prev => {
      const content = [...prev.content];
      if (afterId) {
        const index = content.findIndex(row => row.id === afterId);
        content.splice(index + 1, 0, newRow);
      } else {
        content.push(newRow);
      }
      return { ...prev, content };
    });
  };

  // Remove a content row
  const removeContentRow = (rowId: string) => {
    setDesign(prev => ({
      ...prev,
      content: prev.content.filter(row => row.id !== rowId)
    }));
    // Clear selection if the removed row was selected
    if (selectedRowId === rowId) {
      setSelectedRowId(null);
      setSelectedRowPosition(null);
    }
  };

  // Move row up/down
  const moveRow = (rowId: string, direction: 'up' | 'down') => {
    setDesign(prev => {
      const content = [...prev.content];
      const index = content.findIndex(row => row.id === rowId);
      if (index === -1) return prev;
      
      if (direction === 'up' && index > 0) {
        [content[index - 1], content[index]] = [content[index], content[index - 1]];
      } else if (direction === 'down' && index < content.length - 1) {
        [content[index], content[index + 1]] = [content[index + 1], content[index]];
      }
      
      return { ...prev, content };
    });
  };

  // Handle content edits from preview
  const handleContentEdit = (rowId: string, value: string) => {
    updateRowContent(rowId, value);
  };
  
  // Handle click outside to deselect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is on inline controls
      if (target.closest('.inline-element-controls')) {
        return;
      }
      // Check if click is outside the preview area or on the preview background
      if (previewRef.current && 
          (previewRef.current === target || 
           (previewRef.current.contains(target) && !target.closest('[data-row-id]')))) {
        setSelectedRowId(null);
        setSelectedRowPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Update position when content changes or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (selectedRowId && previewRef.current) {
        const element = previewRef.current.querySelector(`[data-row-id="${selectedRowId}"]`);
        if (element) {
          const rect = element.getBoundingClientRect();
          const previewRect = previewRef.current.getBoundingClientRect();
          setSelectedRowPosition({
            top: rect.top - previewRect.top,
            left: rect.left - previewRect.left,
            width: rect.width,
            height: rect.height
          });
        }
      }
      
      // Update container height
      if (previewContainerRef.current) {
        setContainerHeight(previewContainerRef.current.clientHeight);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [selectedRowId, design.content]);
  
  // Get the currently selected row
  const getSelectedRow = () => {
    if (!selectedRowId) return null;
    return design.content.find(row => row.id === selectedRowId) || null;
  };
  
  // Update style preset
  const updateStylePreset = (preset: string, updates: Partial<TextStyle>) => {
    setDesign(prev => ({
      ...prev,
      template: {
        ...prev.template,
        stylePresets: {
          ...prev.template.stylePresets,
          [preset]: {
            ...prev.template.stylePresets?.[preset],
            ...updates
          }
        }
      }
    }));
  };
  
  


  // Auto-select first template on load only if no initialDesign is provided
  useEffect(() => {
    console.log('=== Auto-template useEffect triggered ===');
    console.log('initialDesign:', !!initialDesign);
    console.log('INVITATION_TEMPLATES_V2.length:', INVITATION_TEMPLATES_V2.length);
    console.log('selectedTemplateId:', selectedTemplateId);
    console.log('Condition check:', !initialDesign && INVITATION_TEMPLATES_V2.length > 0 && !selectedTemplateId);
    
    if (!initialDesign && INVITATION_TEMPLATES_V2.length > 0 && !selectedTemplateId && !forceTemplateSelection) {
      console.log('AUTO-APPLYING first template because no initialDesign');
      const firstTemplate = INVITATION_TEMPLATES_V2[0];
      console.log('First template:', firstTemplate.template.name);
      
      const newDesign = {
        ...firstTemplate,
        metadata: {
          ...firstTemplate.metadata!,
          name: firstTemplate.metadata?.name || 'Untitled Design',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      };
      console.log('Auto-applied design:', newDesign);
      setDesign(newDesign);
      setSelectedTemplateId(firstTemplate.template.id);
    } else {
      console.log('NOT auto-applying template');
    }
  }, [initialDesign, selectedTemplateId, forceTemplateSelection]); // Include dependencies to ensure this runs correctly

  return (
    <div className="h-full bg-gray-50 overflow-auto relative">
      {/* Type and Template Selection Modal */}
      {activeTab === 'types' || activeTab === 'templates' ? (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)', backdropFilter: 'blur(2px)' }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">
                {activeTab === 'types' ? 'Choose Invitation Type' : 'Choose a Template'}
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                {activeTab === 'types' 
                  ? 'Select an invitation type to design. Each type can have different templates and customizations.'
                  : 'Select a pre-designed template to start with. You can customize it further in the next steps.'}
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {activeTab === 'types' && (
                <div>
                  {event.invitationTypes && event.invitationTypes.length > 0 ? (
                    <InvitationTypeListStandalone
                      invitationTypes={event.invitationTypes}
                      onTypeSelect={handleInvitationTypeSelect}
                      selectedTypeId={currentInvitationType?.id}
                    />
                  ) : (
                    <p className="text-gray-500">No invitation types available</p>
                  )}
                </div>
              )}

              {activeTab === 'templates' && (
                <div className="space-y-3">
                  {INVITATION_TEMPLATES_V2.map((template) => (
                    <div
                      key={template.template.id}
                      onClick={() => handleTemplateSelect(template.template.id)}
                      className={`p-4 border rounded-lg transition-colors cursor-pointer ${
                        selectedTemplateId === template.template.id
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-800">{template.template.name}</h4>
                        <p className="text-sm text-gray-600 mt-1 capitalize">
                          {template.template.category} style template
                        </p>
                        {template.metadata?.description && (
                          <p className="text-xs text-gray-500 mt-2">{template.metadata.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setActiveTab(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              {activeTab === 'templates' && (
                <button
                  onClick={() => setActiveTab(null)}
                  disabled={!selectedTemplateId}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Use Template
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Main Preview Area */}
      <div className="h-full p-8" ref={previewContainerRef}>
        <div className="max-w-4xl mx-auto">
          {/* Top action bar with Save/Cancel buttons */}
          <div className={`mb-6 flex items-center ${(isOnboarding || isNewInvitationType) ? 'justify-center' : 'justify-between'}`}>
            {/* Left side - Add Invitation Type button (hidden in onboarding or new invitation type) */}
            {!isOnboarding && !isNewInvitationType && (
              <div>
                <button
                  onClick={() => setShowCreateInvitationTypeModal(true)}
                  disabled={hasChanges}
                  className="px-4 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 hover:shadow-md transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title={hasChanges ? "Save or cancel changes before adding a new invitation type" : ""}
                >
                  Add Invitation Type
                </button>
              </div>
            )}
            
            {/* Right side - Save/Cancel buttons */}
            <div className="flex gap-3">
              {!isOnboarding && !isNewInvitationType && (
                <button
                  onClick={onCancel}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => onSave(design, currentInvitationType)}
                disabled={(isOnboarding || isNewInvitationType) ? false : (!currentInvitationType || !hasChanges)}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                title={(isOnboarding || isNewInvitationType) ? "" : (!hasChanges ? "No changes to save" : "")}
              >
                {(isOnboarding || isNewInvitationType) ? 'Save Design' : (!currentInvitationType ? 'Select Type First' : 'Save Design')}
              </button>
            </div>
          </div>

          {/* Invitation Preview with controls */}
          <div className="flex justify-center">
            <div 
              ref={previewRef}
              className="relative"
              style={{ width: '528px' }}
            >
              <InvitationPreviewV2
                design={design}
                event={event}
                isEditing={true}
                onContentEdit={handleContentEdit}
                onEditStart={handleEditStart}
                onRowSelect={(rowId) => {
                  setSelectedRowId(rowId);
                  // Get the position of the selected element
                  if (previewRef.current) {
                    const element = previewRef.current.querySelector(`[data-row-id="${rowId}"]`);
                    if (element) {
                      const rect = element.getBoundingClientRect();
                      const previewRect = previewRef.current.getBoundingClientRect();
                      setSelectedRowPosition({
                        top: rect.top - previewRect.top,
                        left: rect.left - previewRect.left,
                        width: rect.width,
                        height: rect.height
                      });
                    }
                  }
                }}
                selectedRowId={selectedRowId}
              />
              
              {/* Inline controls overlay */}
              {selectedRowId && selectedRowPosition && getSelectedRow() && (
                <InlineElementControls
                  selectedRow={getSelectedRow()!}
                  theme={design.template.theme}
                  stylePresets={design.template.stylePresets}
                  position={selectedRowPosition}
                  containerHeight={containerHeight}
                  onUpdateRow={updateContentRow}
                  onUpdateRowStyle={updateRowStyle}
                  onAddRow={addContentRow}
                  onRemoveRow={removeContentRow}
                  onMoveRow={moveRow}
                />
              )}
              
              {/* Designer Controls - positioned to the right of invitation */}
              <DesignerControls
                design={design}
                onStylePresetUpdate={updateStylePreset}
                onThemeUpdate={updateTheme}
                onTemplateUpdate={updateTemplate}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Invitation Type Form Modal */}
      <InvitationTypeForm
        eventId={event.id}
        isOpen={showCreateInvitationTypeModal}
        existingTypes={event.invitationTypes || []}
        onSuccess={(invitationType) => {
          setShowCreateInvitationTypeModal(false);
          // You might want to refresh the event data here or update the current selection
          setCurrentInvitationType(invitationType);
        }}
        onCancel={() => setShowCreateInvitationTypeModal(false)}
      />

    </div>
  );
};

export default InvitationDesignerV2;